// Expiry-date rules for the inward modals.
//
// An `<input type="date">` value is a local calendar date string (`YYYY-MM-DD`), never an
// instant, so everything here compares those strings directly. Lexicographic order on
// `YYYY-MM-DD` is chronological order, which sidesteps the timezone traps that come with
// parsing the value into a Date and comparing against `Date.now()`.

const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** The local calendar date of `date`, in the `YYYY-MM-DD` form an `<input type="date">` uses. */
export function toDateInputValue(date: Date): string {
  const year = `${date.getFullYear()}`.padStart(4, "0");
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/**
 * Earliest expiry date an operator may enter: tomorrow. Stock that expires today is already
 * at the end of its life, so inwarding it is treated the same as inwarding expired stock.
 *
 * Feed this to the input's `min` so the native picker greys out everything earlier, and to
 * {@link isExpiryDateAllowed} so a typed or pasted value is rejected too.
 */
export function getMinExpiryDate(now: Date): string {
  const tomorrow = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1
  );

  return toDateInputValue(tomorrow);
}

/**
 * Whether the expiry field holds something submittable.
 *
 * Blank is allowed — the field is optional and left empty for SKUs that are not
 * expiry-tracked. Anything else has to be a well-formed date no earlier than
 * {@link getMinExpiryDate}.
 */
export function isExpiryDateAllowed(
  value: string,
  minExpiryDate: string
): boolean {
  const trimmed = value.trim();

  if (!trimmed) {
    return true;
  }

  if (!DATE_INPUT_PATTERN.test(trimmed)) {
    return false;
  }

  return trimmed >= minExpiryDate;
}

export const EXPIRY_DATE_ERROR = "Expiry date must be a future date.";
